import type { LocationRef } from "../types";

export type LoadBoardProvider = "dat" | "truckstop" | "123loadboard" | "central_dispatch";
export type LoadBoardAction =
  | "draft_post_load"
  | "post_load"
  | "update_posting"
  | "expire_posting"
  | "search_capacity"
  | "rank_capacity"
  | "record_carrier_response";

export interface LoadBoardPostingInput {
  tenantId: string;
  loadId: string;
  equipmentType: string;
  origin: LocationRef;
  destination: LocationRef;
  pickupWindowStart: string;
  pickupWindowEnd: string;
  weightLbs?: number;
  targetRateCents?: number;
}

export interface LoadBoardDraft {
  provider: LoadBoardProvider;
  loadId: string;
  providerTraceId?: string;
  status: "draft";
  createdAt: string;
  normalizedFields: LoadBoardPostingInput;
  validationWarnings: string[];
}

export interface LoadBoardPosting {
  provider: LoadBoardProvider;
  providerPostingId: string;
  loadId: string;
  providerTraceId?: string;
  status: "draft" | "posted" | "updated" | "expired";
  postedAt: string;
  expiresAt: string;
  normalizedFields: LoadBoardPostingInput;
}

export interface LoadBoardPostingUpdateInput {
  providerPostingId: string;
  pickupWindowStart?: string;
  pickupWindowEnd?: string;
  targetRateCents?: number;
  expiresAt?: string;
}

export interface CapacitySearchInput extends Omit<LoadBoardPostingInput, "weightLbs" | "targetRateCents"> {
  maxResults?: number;
}

export interface CapacityResult {
  provider: LoadBoardProvider;
  carrierName: string;
  carrierMc: string;
  contactEmail: string;
  contactPhone: string;
  originDeadheadMiles: number;
  targetRateCents: number;
  fitScore: number;
  evidenceSummary: string;
}

export interface CapacitySearchResult {
  provider: LoadBoardProvider;
  loadId: string;
  providerTraceId?: string;
  searchedAt: string;
  capacity: CapacityResult[];
}

export interface RateSignal {
  provider: LoadBoardProvider;
  lane: {
    origin: LocationRef;
    destination: LocationRef;
  };
  equipmentType: string;
  lowRateCents?: number;
  medianRateCents?: number;
  highRateCents?: number;
  confidenceScore: number;
  capturedAt: string;
}

export interface LoadBoardCarrierResponseInput {
  loadId: string;
  providerPostingId?: string;
  carrierName: string;
  carrierMc?: string;
  contactEmail?: string;
  contactPhone?: string;
  responseType: "call" | "email" | "bid" | "book_now" | "decline" | "other";
  targetRateCents?: number;
  fitScore?: number;
  receivedAt?: string;
  providerPayload?: Record<string, unknown>;
}

export interface LoadBoardCarrierResponse extends LoadBoardCarrierResponseInput {
  provider: LoadBoardProvider;
  providerTraceId?: string;
  receivedAt: string;
}

export class LoadBoardProviderError extends Error {
  constructor(
    readonly provider: LoadBoardProvider,
    readonly code: string,
    message: string,
    readonly retryable: boolean,
    readonly raw?: unknown,
  ) {
    super(message);
    this.name = "LoadBoardProviderError";
  }
}

export interface LoadBoardAdapter {
  provider: LoadBoardProvider;
  draftPostLoad(input: LoadBoardPostingInput): Promise<LoadBoardDraft>;
  postLoad(input: LoadBoardPostingInput): Promise<LoadBoardPosting>;
  updatePosting(input: LoadBoardPostingUpdateInput): Promise<LoadBoardPosting>;
  searchCapacity(input: CapacitySearchInput): Promise<CapacitySearchResult>;
  rankCapacity(capacity: CapacityResult[]): CapacityResult[];
  recordCarrierResponse(input: LoadBoardCarrierResponseInput): Promise<LoadBoardCarrierResponse>;
  expirePosting(providerPostingId: string): Promise<LoadBoardPosting>;
}
